import { useEffect, useMemo, useRef, useState, type ReactElement } from "react";
import {
  fittedModsDeep,
  traderStanding,
  weaponComplete,
  type Inventory,
  type ItemInstance,
  type LoyaltyState,
  type QuestState,
  type WeaponItemDef,
} from "@rietz/shared";
import { uiActions } from "../actions.js";
import {
  openMarketForBuild,
  setWorkbenchOpen,
  setWorkbenchPlan,
  useHud,
  type OpenContainer,
} from "../store.js";
import { TileBody } from "../inventory/tile.js";
import {
  SLOT_LABELS,
  findOwnedWeapon,
  modSlotRows,
  modStatRows,
  verdictFor,
  type ModStatRow,
  type OwnedWeapon,
} from "../inventory/modLogic.js";
import { PREVIEW_H, PREVIEW_W, WeaponPreview } from "../inventory/modPane.js";
import { formatEuros } from "../meta/format.js";
import { CardTitle, Note, Num } from "../meta/kit.js";
import { offerProblem } from "../meta/offerLogic.js";
import {
  explicitPlan,
  nextAssemblyStep,
  partOptions,
  planInstance,
  planRows,
  withPick,
  type BuildPlan,
  type PartOption,
  type PlanRow,
  type PlanStatus,
} from "./buildLogic.js";
import { primaryLabel } from "./workbenchLogic.js";

/**
 * THE WORKBENCH: the whole Gear page given over to one gun. The floating
 * Werkbank changes the gun a part at a time and only ever offers what is
 * already in a pocket; this one PLANS — every mounting point, every part the
 * slot accepts whether owned or not, and the two numbers the decision needs
 * (what it does to the gun, what it costs). Nothing is sent while planning:
 * "Assemble" runs the plan out as ordinary attach/detach ops, and "Buy" hands
 * the shopping list to the market tab.
 *
 * The plan lives in the STORE (`workbench`), so a trip to the market and back
 * does not lose it. The weapon is re-resolved by `screen.tsx` every render and
 * this takes a RESOLVED one, exactly as `ModPane` does.
 */

/** The widest the turntable is allowed to get; past this a gun is just big. */
const VIEWER_MAX_W = 1100;

/** The one stat no attachment moves — printed by the bench's table, but both
 *  columns here would always agree, so it is left out. */
const SKIPPED_STAT = "condition";

export interface WorkbenchScreenProps {
  inv: Inventory;
  stash: OpenContainer | null;
  weapon: OwnedWeapon;
  plan: BuildPlan;
  euros: number;
  level: number;
  quests: readonly QuestState[];
  loyalty: LoyaltyState;
}

export function WorkbenchScreen(props: WorkbenchScreenProps): ReactElement {
  const { item, def } = props.weapon;
  const { inv, stash, plan } = props;

  /** Which row's parts list is showing, by path. */
  const [selected, setSelected] = useState<string | null>(null);
  /** What the last Assemble press did, or `null`. */
  const [message, setMessage] = useState<string | null>(null);

  // A different gun is a different tree, and a different plan.
  useEffect(() => {
    setSelected(null);
    setMessage(null);
  }, [item.iid]);

  const rows = planRows(def, item, plan, inv, stash);
  const planInst = useMemo(() => planInstance(def, item, plan), [def, item, plan]);
  const active = rows.find((row) => row.path === selected) ?? null;
  const primary = primaryLabel(rows);
  const changes = rows.filter((row) => !row.keep).length;
  const toBuy = primary.lines.reduce((n, line) => n + line.count, 0);

  const weaponIid = item.iid;
  const assemble = (): void => {
    // Spelled out ONCE against the gun as it stands: a path the plan leaves
    // implicit stops meaning anything the moment its host comes off.
    const start = useHud.getState();
    const first =
      start.inv === null ? null : findOwnedWeapon(start.inv, weaponIid, start.container);
    if (start.inv === null || first === null) return;
    const spelled = explicitPlan(
      first.def,
      first.item,
      start.workbench?.plan ?? {},
      start.inv,
      start.container,
    );

    // Against the LIVE store, not this render's props: every op replaces the
    // inventory, and `tryOp` is synchronous, so the loop reads its own effects.
    for (let i = 0; i < 64; i++) {
      const state = useHud.getState();
      const live = state.inv;
      const owned = live === null ? null : findOwnedWeapon(live, weaponIid, state.container);
      if (live === null || owned === null) return;
      const step = nextAssemblyStep(owned.def, owned.item, live, state.container, spelled);
      if (step.t === "done") {
        setWorkbenchPlan({});
        setMessage("Assembled.");
        return;
      }
      if (step.t === "blocked") {
        setMessage(step.reason);
        return;
      }
      uiActions().tryOp(step.op);
      // A refused op leaves the believed inventory untouched. Spinning on it
      // would burn all 64 turns on the same rejection.
      if (useHud.getState().inv === live) {
        setMessage("The workbench refused an operation.");
        return;
      }
    }
  };

  return (
    <div className="wb">
      <header className="wb-top">
        <button
          type="button"
          className="metabtn metabtn-ghost"
          onClick={() => {
            setWorkbenchOpen(false);
          }}
        >
          ← Gear
        </button>
        <span style={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 0 }}>
          <span className="mm-eyebrow">Workbench</span>
          <span className="mm-title mm-title-lg">{def.name}</span>
        </span>

        <div className="wb-top-right">
          <span className="wb-summary">
            <Note>
              {changes === 0 ? "No changes planned" : `${changes} change${changes === 1 ? "" : "s"}`}
              {toBuy === 0 ? "" : ` · ${toBuy} to buy · ${formatEuros(primary.cost)}`}
            </Note>
            {/* The bench's half of `acceptFire`'s "incomplete", asked of the
                PLAN — a gun the player is about to build must not surprise them
                in a raid. */}
            {weaponComplete(def, planInst) ? null : (
              <Num tone="bad" style={{ fontSize: 11 }}>
                Incomplete — this weapon will not fire.
              </Num>
            )}
            {message === null ? null : <Note style={{ fontSize: 11 }}>{message}</Note>}
          </span>
          {Object.keys(plan).length === 0 ? null : (
            <button
              type="button"
              className="metabtn metabtn-ghost"
              onClick={() => {
                setWorkbenchPlan({});
                setMessage(null);
              }}
            >
              Reset
            </button>
          )}
          <button
            type="button"
            className="metadeploy"
            disabled={primary.disabled}
            title={primary.title}
            onClick={() => {
              setMessage(null);
              if (primary.action === "buy") {
                openMarketForBuild(primary.lines.map((line) => line.def.id));
              } else if (primary.action === "assemble") {
                assemble();
              }
            }}
          >
            {primary.label}
          </button>
        </div>
      </header>

      <div className="wb-body">
        <section className="mm-card" style={{ flex: 1, minWidth: 0, overflowY: "auto" }}>
          <Viewer
            def={def}
            planInst={planInst}
            inv={inv}
            stash={stash}
            // Only a GUN-level row has a callout, and its path IS its slot id.
            selected={active === null || active.depth !== 0 ? null : active.slot.id}
            onSelect={(slot) => {
              setSelected(slot);
            }}
          />
          <CardTitle>Weapon characteristics</CardTitle>
          <StatTable def={def} item={item} planInst={planInst} />
        </section>

        <section className="mm-card" style={{ flex: "0 0 380px", overflowY: "auto" }}>
          <CardTitle aside={<Note>{rows.length}</Note>}>Assembly</CardTitle>
          {rows.length === 0 ? (
            <Note>This weapon does not accept attachments.</Note>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
              {rows.map((row) => (
                <AssemblyRow
                  key={row.key}
                  row={row}
                  selected={row.path === selected}
                  onSelect={() => {
                    setSelected(row.path === selected ? null : row.path);
                  }}
                />
              ))}
            </div>
          )}

          {active === null ? null : (
            <>
              <CardTitle>{`Parts · ${SLOT_LABELS[active.slot.id]}`}</CardTitle>
              <PartList
                row={active}
                rows={rows}
                weapon={props.weapon}
                inv={inv}
                stash={stash}
                plan={plan}
                euros={props.euros}
                level={props.level}
                quests={props.quests}
                loyalty={props.loyalty}
              />
            </>
          )}
        </section>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// The turntable
// ---------------------------------------------------------------------------

/**
 * The floating bench's 640×280 viewport, CSS-scaled to whatever the page can
 * spare. The renderer is told the scale, so blowing it up reveals pixels rather
 * than stretching them. It draws the PLAN, not the gun.
 */
function Viewer(props: {
  def: WeaponItemDef;
  planInst: ItemInstance;
  inv: Inventory;
  stash: OpenContainer | null;
  selected: string | null;
  onSelect: (slot: string | null) => void;
}): ReactElement {
  const hostRef = useRef<HTMLDivElement | null>(null);
  const [width, setWidth] = useState(PREVIEW_W);

  // Guarded: these screens are also rendered to static markup in tests, where
  // there is no observer and no layout.
  useEffect(() => {
    const host = hostRef.current;
    if (host === null || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver((entries) => {
      const box = entries[0]?.contentRect;
      if (box !== undefined && box.width > 0) setWidth(box.width);
    });
    observer.observe(host);
    return () => {
      observer.disconnect();
    };
  }, []);

  const scale = Math.min(width, VIEWER_MAX_W) / PREVIEW_W;

  return (
    <div ref={hostRef} style={{ width: "100%" }}>
      <div className="wb-viewer" style={{ width: PREVIEW_W * scale, height: PREVIEW_H * scale }}>
        <div style={{ transform: `scale(${scale})`, transformOrigin: "top left" }}>
          <WeaponPreview
            weaponIid={props.planInst.iid}
            defId={props.def.id}
            mods={fittedModsDeep(props.def, props.planInst).map((fitted) => fitted.def.id)}
            rows={modSlotRows(props.def, props.planInst, props.inv, props.stash)}
            drag={null}
            displayScale={scale}
            readOnly
            selected={props.selected}
            onSelect={props.onSelect}
          />
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// The numbers
// ---------------------------------------------------------------------------

/**
 * The gun as it is against the gun as planned, row by row. `verdictFor` scores
 * the PAIR — a row's own `verdict` is against the bare def, which is a
 * different question.
 */
function StatTable(props: {
  def: WeaponItemDef;
  item: ItemInstance;
  planInst: ItemInstance;
}): ReactElement {
  const now = modStatRows(props.def, props.item);
  const next = modStatRows(props.def, props.planInst);
  const byKey = new Map(next.map((row) => [row.key, row]));

  return (
    <div style={{ display: "flex", flexDirection: "column" }}>
      {now
        .filter((row) => row.key !== SKIPPED_STAT)
        .map((row) => {
          const planned = byKey.get(row.key) ?? row;
          const verdict = verdictFor(row.current, planned.current, row.dir);
          return (
            <div className="wb-stat" key={row.key}>
              <span style={{ color: "var(--mm-text-dim)" }}>{row.label}</span>
              <Num tone="dim">{row.currentText}</Num>
              <span className="wb-stat-arrow">→</span>
              <Num tone={verdict === 1 ? "good" : verdict === -1 ? "bad" : "default"}>
                {planned.currentText}
              </Num>
            </div>
          );
        })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// The tree, and what may go in it
// ---------------------------------------------------------------------------

/** What each status says on its badge. A `buy` row prints its own price. */
const STATUS_WORDS: Record<PlanStatus, string> = {
  fitted: "on the gun",
  owned: "owned",
  buy: "",
  unavailable: "not for sale",
  empty: "—",
};

function AssemblyRow(props: {
  row: PlanRow;
  selected: boolean;
  onSelect: () => void;
}): ReactElement {
  const { row } = props;
  const missingVital = row.vital && row.planned === null;
  const badge =
    row.status === "buy" && row.price !== null ? formatEuros(row.price) : STATUS_WORDS[row.status];

  return (
    <button
      type="button"
      className="wb-row"
      data-depth={row.depth}
      aria-selected={props.selected}
      onClick={props.onSelect}
      title={`${SLOT_LABELS[row.slot.id]}${row.vital ? " (vital)" : ""} · ${
        row.planned?.name ?? "Empty"
      }`}
    >
      <span className="wb-slot">
        {row.depth === 0 ? "" : "└ "}
        {SLOT_LABELS[row.slot.id]}
        {row.vital ? " · vital" : ""}
      </span>
      <span className="wb-row-name" style={missingVital ? { color: "var(--mm-bad)" } : undefined}>
        {row.planned?.name ?? "Empty"}
      </span>
      <span className="wb-tag" data-status={row.status}>
        {badge}
      </span>
    </button>
  );
}

function PartList(props: {
  row: PlanRow;
  rows: readonly PlanRow[];
  weapon: OwnedWeapon;
  inv: Inventory;
  stash: OpenContainer | null;
  plan: BuildPlan;
  euros: number;
  level: number;
  quests: readonly QuestState[];
  loyalty: LoyaltyState;
}): ReactElement {
  const { row, weapon, plan } = props;
  const options = partOptions(row, props.rows, props.inv, props.stash);
  // The plan AS IT STANDS, which every option is measured against.
  const base = useMemo(
    () => modStatRows(weapon.def, planInstance(weapon.def, weapon.item, plan)),
    [weapon.def, weapon.item, plan],
  );

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      {/* Emptying a VITAL slot is not an option — the gun would not fire, and
          the menu's rule is absent rather than disabled. */}
      {row.vital ? null : (
        <button
          type="button"
          className="wb-part"
          aria-selected={row.planned === null}
          onClick={() => {
            setWorkbenchPlan(withPick(plan, row.path, null));
          }}
        >
          <span className="wb-thumb" />
          <span className="wb-part-body">
            <span className="wb-part-name">Empty</span>
            <Note style={{ fontSize: 11 }}>Leave this mounting point bare</Note>
          </span>
        </button>
      )}

      {options.map((option) => (
        <button
          key={option.def.id}
          type="button"
          className="wb-part"
          aria-selected={option.picked}
          title={option.def.description ?? option.def.name}
          onClick={() => {
            setWorkbenchPlan(withPick(plan, row.path, option.def.id));
          }}
        >
          <span className="wb-thumb">
            <TileBody
              def={option.def}
              item={{ iid: `opt:${option.def.id}`, defId: option.def.id, stack: 1 }}
              iconOnly
              cursor="pointer"
            />
          </span>
          <span className="wb-part-body">
            <span className="wb-part-name">{option.def.name}</span>
            <Note style={{ fontSize: 11 }}>{sourceLine(option, props)}</Note>
            <Deltas
              weapon={weapon}
              base={base}
              plan={withPick(plan, row.path, option.def.id)}
            />
          </span>
        </button>
      ))}
    </div>
  );
}

/**
 * Where this part would come from, in one phrase — and, for a shelf line,
 * whatever stands between the player and it. Money and room do not count: both
 * are one raid or one drag away, and the market says so itself.
 */
function sourceLine(
  option: PartOption,
  props: {
    euros: number;
    level: number;
    quests: readonly QuestState[];
    loyalty: LoyaltyState;
    stash: OpenContainer | null;
  },
): string {
  if (option.onGun) return "On the gun";
  if (option.owned > 0) return `Owned ×${option.owned}`;
  const offer = option.offer;
  if (offer === null) return "Not for sale";
  const problem = offerProblem(
    offer.trader,
    option.def.id,
    props.euros,
    props.stash?.grid ?? null,
    traderStanding(props.loyalty, props.quests, props.level, offer.trader.id),
  );
  const gate =
    problem === null || problem === "Not enough money" || problem === "No room in stash"
      ? ""
      : ` · ${problem}`;
  return `${formatEuros(offer.price)} · ${offer.trader.name}${gate}`;
}

/** What picking this part would move, and which way. Only the rows that change
 *  are drawn — a list of nine "unchanged" is a list nobody reads. */
function Deltas(props: {
  weapon: OwnedWeapon;
  base: readonly ModStatRow[];
  plan: BuildPlan;
}): ReactElement | null {
  const { def, item } = props.weapon;
  const plan = props.plan;
  const next = useMemo(() => modStatRows(def, planInstance(def, item, plan)), [def, item, plan]);
  const byKey = new Map(next.map((row) => [row.key, row]));

  const moved = props.base
    .filter((row) => row.key !== SKIPPED_STAT)
    .map((row) => {
      const planned = byKey.get(row.key) ?? row;
      return { row, planned, verdict: verdictFor(row.current, planned.current, row.dir) };
    })
    .filter((entry) => entry.verdict !== 0);
  if (moved.length === 0) return null;

  return (
    <span className="wb-deltas">
      {moved.map((entry) => (
        <span
          key={entry.row.key}
          className="wb-delta"
          style={{ color: entry.verdict === 1 ? "var(--mm-good)" : "var(--mm-bad)" }}
        >
          {entry.row.label} {entry.planned.currentText}
        </span>
      ))}
    </span>
  );
}
